import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ListPagination } from '@/components/admin/shared/ListPagination';
import { ListSearchInput } from '@/components/admin/shared/ListSearchInput';
import { QueryErrorState } from '@/components/admin/shared/QueryErrorState';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { Card } from '@/components/ui/card';
import { ADMIN_LIST_PAGE_SIZE, SEARCH_DEBOUNCE_MILLISECONDS } from '@/constants/adminPagination';
import {
  ALL_EMAIL_LOG_STATUSES,
  type EmailLogStatusFilter as StatusFilterValue,
} from '@/constants/emailLog';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';
import { useWorkspaceEmailLogQuery } from '@/hooks/useWorkspaceEmailLogQueries';
import { EmailLogEntryModal } from './EmailLogEntryModal';
import { EmailLogEntryRow } from './EmailLogEntryRow';
import { EmailLogStatusFilter } from './EmailLogStatusFilter';

export function EmailLogSection() {
  const { t } = useTranslation('emailLog');
  const [searchInput, setSearchInput] = useState('');
  const debouncedSearch = useDebouncedValue(searchInput, SEARCH_DEBOUNCE_MILLISECONDS);
  const [statusFilter, setStatusFilter] = useState<StatusFilterValue>(ALL_EMAIL_LOG_STATUSES);
  const [page, setPage] = useState(1);
  const [openEntryId, setOpenEntryId] = useState<string | null>(null);
  const entriesQuery = useWorkspaceEmailLogQuery({
    page,
    pageSize: ADMIN_LIST_PAGE_SIZE,
    search: debouncedSearch,
    status: statusFilter === ALL_EMAIL_LOG_STATUSES ? null : statusFilter,
  });
  const hasActiveFilters =
    debouncedSearch.trim().length > 0 || statusFilter !== ALL_EMAIL_LOG_STATUSES;

  function changeSearch(nextSearch: string) {
    setSearchInput(nextSearch);
    setPage(1);
  }

  function changeStatusFilter(nextStatusFilter: StatusFilterValue) {
    setStatusFilter(nextStatusFilter);
    setPage(1);
  }

  return (
    <div>
      <p className="text-[13px] text-zoomuted mb-4 max-w-3xl">{t('intro')}</p>
      <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_auto] gap-3 items-start mb-5">
        <ListSearchInput
          value={searchInput}
          onChange={changeSearch}
          placeholder={t('searchPlaceholder')}
        />
        <EmailLogStatusFilter value={statusFilter} onChange={changeStatusFilter} />
      </div>
      <EmailLogResults
        entriesQuery={entriesQuery}
        hasActiveFilters={hasActiveFilters}
        page={page}
        onPageChange={setPage}
        onOpenEntry={setOpenEntryId}
      />
      {openEntryId === null ? null : (
        <EmailLogEntryModal
          entryId={openEntryId}
          onClose={function closeEntry() {
            setOpenEntryId(null);
          }}
        />
      )}
    </div>
  );
}

interface EmailLogResultsProps {
  entriesQuery: ReturnType<typeof useWorkspaceEmailLogQuery>;
  hasActiveFilters: boolean;
  page: number;
  onPageChange: (page: number) => void;
  onOpenEntry: (entryId: string) => void;
}

function EmailLogResults({
  entriesQuery,
  hasActiveFilters,
  page,
  onPageChange,
  onOpenEntry,
}: EmailLogResultsProps) {
  const { t } = useTranslation('emailLog');
  const result = entriesQuery.data;

  if (entriesQuery.isLoading === true) {
    return (
      <div className="py-16 flex justify-center">
        <LoadingSpinner className="w-7 h-7" />
      </div>
    );
  }

  if (entriesQuery.isError === true || result === undefined) {
    return (
      <QueryErrorState
        onRetry={function retryEmailLog() {
          entriesQuery.refetch();
        }}
      />
    );
  }

  if (result.items.length === 0) {
    return (
      <Card padding="lg" shadow={false} className="text-center text-sm text-zoomuted">
        {hasActiveFilters === true ? t('noResults') : t('empty')}
      </Card>
    );
  }

  return (
    <>
      <Card padding="none" className="overflow-hidden">
        <ul className="divide-y divide-zooborder">
          {result.items.map(function renderEntry(entry) {
            return <EmailLogEntryRow key={entry.id} entry={entry} onOpen={onOpenEntry} />;
          })}
        </ul>
      </Card>
      <ListPagination
        page={page}
        pageSize={ADMIN_LIST_PAGE_SIZE}
        totalItems={result.totalItems}
        onPageChange={onPageChange}
      />
    </>
  );
}
